import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { useSubscriptionTier } from '../contexts/SubscriptionTierContext';
import { useAuth } from '../contexts/AuthContext';
import type { Pet, AsyncStatus } from '../types';
import { BASIC_VAULT_MAX_PETS } from '../utils/constants';
import { UpgradeRequiredError } from '../utils/upgradeErrors';

// ─── Types ──────────────────────────────────────────────────────────────────

/** Postgres row shape — column is `weight_kg`, not `weight`. */
interface PetRow {
  id: string;
  user_id: string;
  name: string;
  breed: string;
  species: Pet['species'];
  date_of_birth: string;
  weight_kg: number | null;
}

function petFromRow(row: PetRow): Pet {
  return {
    id: row.id,
    user_id: row.user_id,
    name: row.name,
    breed: row.breed,
    species: row.species,
    date_of_birth: row.date_of_birth,
    weight: row.weight_kg,
  };
}

/** Fields required to create a new pet — user_id is injected from auth */
export interface NewPetPayload {
  name: string;
  breed: string;
  species: Pet['species'];
  date_of_birth: string; // ISO 8601 date string
  weight: number | null;
}

interface UsePetsReturn {
  pets: Pet[];
  status: AsyncStatus;
  error: string | null;
  /** Refresh the pet list from Supabase */
  refetch: () => Promise<void>;
  /** Insert a new pet row; returns the created pet or throws */
  addPet: (payload: NewPetPayload) => Promise<Pet>;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * usePets — manages the authenticated user's pet list.
 *
 * All queries include `.eq('user_id', user.id)` so that even if
 * Supabase RLS is misconfigured, the frontend never requests
 * another user's data.
 */
export function usePets(): UsePetsReturn {
  const { user } = useAuth();
  const { tier, ready } = useSubscriptionTier();
  const { showUpgrade } = useUpgradeModal();

  const [pets, setPets] = useState<Pet[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const fetchPets = useCallback(async () => {
    if (!user) return;

    setStatus('loading');
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('pets')
      .select('*')
      .eq('user_id', user.id)          // RLS-aligned client-side filter
      .order('name', { ascending: true });

    if (fetchError) {
      setError(fetchError.message);
      setStatus('error');
    } else {
      setPets((data as PetRow[] | null)?.map(petFromRow) ?? []);
      setStatus('success');
    }
  }, [user]);

  // Fetch on mount and whenever the authenticated user changes
  useEffect(() => {
    void fetchPets();
  }, [fetchPets]);

  const addPet = useCallback(
    async (payload: NewPetPayload): Promise<Pet> => {
      if (!user) throw new Error('Not authenticated');

      if (ready && tier === 'basic' && pets.length >= BASIC_VAULT_MAX_PETS) {
        showUpgrade('pets');
        throw new UpgradeRequiredError('pets');
      }

      const { weight, ...rest } = payload;

      const { data, error: insertError } = await supabase
        .from('pets')
        .insert({
          ...rest,
          user_id: user.id,
          weight_kg: weight,
        })
        .select()
        .single();

      if (insertError) throw new Error(insertError.message);

      const created = petFromRow(data as PetRow);
      // Optimistically append to local state so the UI updates immediately
      setPets((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      return created;
    },
    [user, ready, tier, pets.length, showUpgrade]
  );

  return {
    pets,
    status,
    error,
    refetch: fetchPets,
    addPet,
  };
}
